import type { Tone } from '../types';

type Props = {
  label: string;
  ratio?: string;
  tone?: Tone;
  className?: string;
  /** When provided, the real image renders instead of the striped placeholder. */
  src?: string | null;
  /** Alt text override for the real image. Falls back to `label`. */
  alt?: string;
  /**
   * How the real image fits its slot.
   * - 'cover' (default): fills the slot, cropping if needed.
   * - 'contain': shows the entire image, may letterbox.
   */
  fit?: 'cover' | 'contain';
};

const COLORS: Record<Tone, { bg: string; line: string; text: string }> = {
  vert: { bg: '#e8efe9', line: 'rgba(11,93,59,.18)', text: 'rgba(15,25,22,.55)' },
  rouge: { bg: '#f7e6e8', line: 'rgba(200,16,46,.16)', text: 'rgba(60,15,20,.55)' },
  creme: { bg: '#f1ebde', line: 'rgba(120,90,30,.18)', text: 'rgba(50,40,15,.55)' },
  encre: { bg: '#1a2421', line: 'rgba(255,255,255,.07)', text: 'rgba(255,255,255,.55)' },
  or: { bg: '#f1ebde', line: 'rgba(120,90,30,.18)', text: 'rgba(50,40,15,.55)' },
};

export function ImgPlaceholder({
  label,
  ratio = '16/9',
  tone = 'vert',
  className = '',
  src,
  alt,
  fit = 'cover',
}: Props) {
  // Real image path — render an actual <img>, keep the same outer wrapping
  // so existing CSS positioning (overlays, ratios, tags…) keeps working.
  if (src) {
    return (
      <div
        className={`mtc-imgph mtc-imgph-real ${className}`}
        style={{ aspectRatio: ratio, borderRadius: 4, overflow: 'hidden' }}
      >
        <img
          src={src}
          alt={alt ?? label}
          loading="lazy"
          style={{
            width: '100%',
            height: '100%',
            objectFit: fit,
            display: 'block',
          }}
        />
      </div>
    );
  }

  const c = COLORS[tone];
  return (
    <div
      className={`mtc-imgph ${className}`}
      style={{
        aspectRatio: ratio,
        background: `repeating-linear-gradient(135deg, ${c.bg} 0 14px, ${c.line} 14px 15px)`,
        color: c.text,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 11,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        borderRadius: 4,
      }}
    >
      {label}
    </div>
  );
}
